import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Button, Form, Spin, Table } from 'antd'
import { format } from 'date-fns'

import { formatMoney, formatSum } from '../../utils/number-utils'
import { userSelector } from '../../store/session'
import ProjectSelector from '../ProjectSelector'

const API_URL = process.env.REACT_APP_API_URL

const getVatType = (vatType) => {
  switch (vatType) {
    case 'none':
      return 'Без НДС'
    case 'up':
      return 'НДС сверху'
    case 'include':
      return 'НДС в сумме'
    default:
      return ''
  }
}

const numberCellStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  overflowWrap: 'normal',
}

const columnsItems = [
  {
    title: 'Позиция',
    dataIndex: 'estimate',
    key: 'estimate_name',
    render: (estimate) => estimate?.name,
  },
  {
    title: 'Категория',
    dataIndex: 'estimate',
    key: 'estimate_category',
    render: (estimate) => estimate?.category,
  },
  {
    title: 'Кол-во смета',
    dataIndex: 'estimate',
    key: 'estimate_amount',
    width: 120,
    render: (estimate) => <div style={numberCellStyle}>{formatSum(estimate?.amount)}</div>,
  },
  {
    title: 'Кол-во акт',
    dataIndex: 'amount',
    key: 'amount',
    width: 100,
    render: (amount) => <div style={numberCellStyle}>{formatSum(amount)}</div>,
  },
  {
    title: 'Ед. изм.',
    dataIndex: 'estimate',
    key: 'estimate_unit',
    width: 100,
    render: (estimate) => estimate?.unit,
  },
  {
    title: 'Цена за единицу',
    dataIndex: 'price',
    key: 'price',
    render: (price) => formatMoney(price),
  },
  {
    title: 'Сумма',
    dataIndex: 'price',
    key: 'sum',
    render: (price, record) => {
      if (price > 0 && record?.amount > 0) {
        return <div style={{ whiteSpace: 'nowrap' }}>{formatMoney(price * record?.amount)}</div>
      }
    },
  },
  {
    title: 'Тип НДС',
    dataIndex: 'vatType',
    key: 'vatType',
    render: (vatType) => getVatType(vatType),
  },
  {
    title: '% НДС',
    dataIndex: 'vatValue',
    key: 'vatValue',
    render: (vatValue, record) => (vatValue ? `${vatValue} %` : ''),
  },
  {
    title: 'НДС',
    dataIndex: 'vatValue',
    key: 'vatCalc',
    render: (vatValue, record) => {
      const finalAmount = record.amount
      if (record.vatType === 'up' && finalAmount > 0 && vatValue > 0 && record.price > 0) {
        const sum = record.price * finalAmount
        const vat = (sum / 100) * vatValue
        return formatMoney(vat)
      } else if (record.vatType === 'include' && finalAmount > 0 && vatValue > 0 && record.price > 0) {
        const sum = record.price * finalAmount
        const vat = (sum * vatValue) / (100 + vatValue)
        return formatMoney(vat)
      }
      return ''
    },
  },
  {
    title: 'Всего с НДС',
    dataIndex: 'vatValue',
    key: 'vatSum',
    render: (vatValue, record) => {
      const finalAmount = record.amount
      if (record.vatType === 'up' && vatValue > 0 && record.price > 0 && finalAmount > 0) {
        const sum = record.price * finalAmount
        const vat = (sum / 100) * vatValue
        return formatMoney(sum + vat)
      } else if (record.vatType === 'include' && record.price > 0 && finalAmount > 0) {
        return formatMoney(record.price * finalAmount)
      }
      return ''
    },
  },
]

const ActTable = ({ acts, isLoading, tablePagination, onFilterFormSubmit, onFilterFormClear, onAction }) => {
  const [form] = Form.useForm()

  const { user } = useSelector(userSelector)

  const isProject = useMemo(() => user?.role?.type === 'project_manager', [user])
  const isEstimator = useMemo(() => user?.role?.type === 'estimator', [user])

  const columns = [
    {
      title: 'Номер',
      dataIndex: 'number',
      key: 'number',
      width: 150,
    },
    {
      title: 'Дата',
      dataIndex: 'date',
      key: 'date',
      width: 100,
      render: (date) => format(new Date(date), 'dd-MM-yyyy'),
    },
    {
      title: 'Создан',
      dataIndex: 'user',
      key: 'user',
      width: 200,
      render: (user) => `${user.first_name} ${user.second_name}`,
    },
    {
      title: 'Проект',
      dataIndex: 'project',
      key: 'project',
      width: 400,
      render: (project) => project?.name,
    },
    {
      title: 'Документы',
      dataIndex: 'file',
      key: 'file',
      render: (file) => {
        return (
          <a href={`${API_URL}${file?.url}`} target="_blank" rel="noreferrer">
            {file?.name}
          </a>
        )
      },
    },
    {
      title: 'Действия',
      key: 'action',
      align: 'center',
      width: 150,
      render: (record) => {
        return (
          <Button type="link" disabled={!isProject && !isEstimator} onClick={() => onAction(record)}>
            Редактировать
          </Button>
        )
      },
    },
  ]

  const expandable = {
    expandedRowRender: (record) => (
      <Table
        style={{ padding: '15px 0', marginLeft: -25 }}
        size="small"
        bordered
        columns={columnsItems}
        pagination={false}
        dataSource={record?.act_items}
      />
    ),
    rowExpandable: (record) => record?.act_items?.length > 0,
  }

  const onFilterClear = () => {
    onFilterFormClear()
    form.resetFields()
  }

  return (
    <Spin spinning={isLoading}>
      <div style={{ paddingTop: 10 }}>
        <Form form={form} layout="vertical" style={{ display: 'flex' }} onFinish={onFilterFormSubmit}>
          <Form.Item style={{ minWidth: 250 }} label="Проект" name="projects">
            <ProjectSelector mode="multiple" />
          </Form.Item>
          <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
            <Button onClick={() => form.submit()}>Применить</Button>
          </Form.Item>
          <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
            <Button type="dashed" onClick={onFilterClear}>
              Очистить
            </Button>
          </Form.Item>
        </Form>
      </div>
      <Table
        rowKey="id"
        expandable={expandable}
        size="small"
        bordered
        style={{ marginTop: '20px' }}
        pagination={tablePagination}
        columns={columns}
        dataSource={acts}
      />
    </Spin>
  )
}

export default ActTable
